import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { formatDateTimeID } from "@/lib/format";
import { Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const MODULES = [
  "Autentikasi", "Manajemen Pengguna", "Manajemen Sesi", "Pengaturan", "Profil Saya",
];

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ module: "all", activity: "", user_email: "" });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.module !== "all") params.module = filter.module;
      if (filter.activity) params.activity = filter.activity;
      if (filter.user_email) params.user_email = filter.user_email;
      const { data } = await api.get("/audit-logs", { params });
      setLogs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Jejak Audit</h1>
        <p className="text-slate-500 mt-2">Mencatat aktivitas dan perubahan penting yang terjadi di dalam sistem.</p>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Modul</Label>
              <Select value={filter.module} onValueChange={(v) => setFilter({ ...filter, module: v })}>
                <SelectTrigger data-testid="audit-module-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Modul</SelectItem>
                  {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Aktivitas</Label>
              <Input placeholder="Contoh: Berhasil Masuk" value={filter.activity} onChange={(e) => setFilter({ ...filter, activity: e.target.value })} data-testid="audit-activity-filter" />
            </div>
            <div>
              <Label className="text-xs">Email Pengguna</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9" value={filter.user_email} onChange={(e) => setFilter({ ...filter, user_email: e.target.value })} data-testid="audit-user-filter" />
              </div>
            </div>
            <div className="flex items-end">
              <Button onClick={load} className="w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="audit-apply-filter">Saring</Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Waktu</TableHead>
                  <TableHead>Pengguna</TableHead>
                  <TableHead>Modul</TableHead>
                  <TableHead>Aktivitas</TableHead>
                  <TableHead>Referensi</TableHead>
                  <TableHead>Alamat IP</TableHead>
                  <TableHead className="text-right">Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Memuat data...</TableCell></TableRow>
                ) : logs.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Belum ada aktivitas.</TableCell></TableRow>
                ) : logs.map((l) => (
                  <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                    <TableCell className="whitespace-nowrap text-xs">{formatDateTimeID(l.timestamp)}</TableCell>
                    <TableCell>
                      <div className="text-sm">{l.user_name || "Sistem"}</div>
                      <div className="text-xs text-slate-500">{l.user_email || "-"}</div>
                    </TableCell>
                    <TableCell>{l.module}</TableCell>
                    <TableCell className="font-medium">{l.activity}</TableCell>
                    <TableCell className="text-xs text-slate-600">{l.reference || "-"}</TableCell>
                    <TableCell className="text-xs font-mono-num">{l.ip || "-"}</TableCell>
                    <TableCell className="text-right">
                      {(l.before || l.after) ? (
                        <Button variant="outline" size="sm" onClick={() => setDetail(l)} data-testid={`audit-detail-${l.id}`}>Lihat</Button>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white max-w-3xl">
          <DialogHeader><DialogTitle>Detail Perubahan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Data Sebelum</div>
              <pre className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs overflow-auto max-h-80">{JSON.stringify(detail?.before || {}, null, 2)}</pre>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Data Sesudah</div>
              <pre className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs overflow-auto max-h-80">{JSON.stringify(detail?.after || {}, null, 2)}</pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
